import { useRoleAccess } from 'hooks/useRoleAccess';
import React, { useContext, useEffect, useReducer, useState } from 'react';
import { pushesViewReducer } from './pushesViewReducer';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	IRestaurantFullInfoContext,
	RestaurantFullInfoContext,
} from 'modules/CustomersAndRestaurants/Restaurants/RestaurantFullInfo/restaurantFullInfoState/restaurantFullInfoContext';
import {
	PushesViewContext,
	pushesViewInitialState,
	TPushesViewCountOfItems,
	TPushesViewSortBy,
	TTypeOfModalWindow,
} from './pushesViewContext';
import { PushesViewActionTypes } from '../pushesViewActions';
import { useFetchData } from 'hooks/useFetchData';
import {
	UserRoles,
	IGetPaginatedRestaurantSoftwareUpdates,
	ISoftwareUpdates,
	RestaurantNotificationEventType,
} from 'types';
import { getPaginatedRestaurantSoftwareUpdates } from 'backend/restaurants';
import { AxiosResponse } from 'axios';

export const PushesViewState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(pushesViewReducer, pushesViewInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { restaurantForFullInfo } = useContext<IRestaurantFullInfoContext>(RestaurantFullInfoContext);
	const hasAccess = useRoleAccess([UserRoles.SUPER_USER, UserRoles.TECHNICAL]);
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);

	const { refetch, response, loading } = useFetchData<
		AxiosResponse<ISoftwareUpdates>,
		IGetPaginatedRestaurantSoftwareUpdates
	>(hasAccess && restaurantForFullInfo?.id ? getPaginatedRestaurantSoftwareUpdates : null, {
		restaurantId: restaurantForFullInfo?.id || '',
		pageNumber: state.currentPage,
		pageSize: Number(state.countOfItems),
		searchByName: state.searchString ? state.searchString : null,
		sortName: state.sortBy === 'Name1-10' || state.sortBy === 'Name10-1' ? state.sortBy : null,
		sortLastUpdateDate:
			state.sortBy === 'LastUpdate1-10' || state.sortBy === 'LastUpdate10-1' ? state.sortBy : null,
		sortUpdateBy: state.sortBy === 'UpdatedByA-Z' || state.sortBy === 'UpdatedByZ-A' ? state.sortBy : null,
		sortSoftwareType:
			state.sortBy === 'SoftwareTypeA-Z' || state.sortBy === 'SoftwareTypeZ-A' ? state.sortBy : null,
	});

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: PushesViewActionTypes.SET_SOFTWARE_UPDATES,
				payload: {
					softwareUpdates: response.data,
				},
			});
		}
	}, [response]);

	const changeSearchString = (value: string) => {
		dispatch({
			type: PushesViewActionTypes.CHANGE_SEARCH_STRING,
			payload: {
				searchString: value,
			},
		});
	};

	const setSortBy = (sortBy: TPushesViewSortBy) => {
		dispatch({
			type: PushesViewActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TPushesViewCountOfItems) => {
		dispatch({
			type: PushesViewActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: PushesViewActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setPushEventType = (value: RestaurantNotificationEventType | null) => {
		dispatch({
			type: PushesViewActionTypes.SET_PUSH_EVENT_TYPE,
			payload: {
				pushEventType: value,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: PushesViewActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	useEffect(() => {
		if (isFirstRender) {
			setIsFirstRender(false);
			return;
		}
		refetch();
	}, [state.currentPage, state.countOfItems, state.searchString, state.sortBy, restaurantForFullInfo?.id, hasAccess]);

	return (
		<PushesViewContext.Provider
			value={{
				changeSearchString,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setPushEventType,
				changeTypeOfModalWindow,
				searchString: state.searchString,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				pushEventType: state.pushEventType,
				softwareUpdates: state.softwareUpdates,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</PushesViewContext.Provider>
	);
};
